import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FaSave, FaShieldAlt, FaLock } from "react-icons/fa";
import useAuth from "../../Hooks/useAuth";
import usePermission from "../../Hooks/usePermission";
import { useAlert } from "../../context/AlertContext";
import {
  useGetAvailablePermissionsQuery,
  useUpdateUserPermissionsMutation,
} from "../../store";
import { MascotLoadingOverlay } from "../shared";
// Kategori basliklari, sirasi ve izin etiketleri ORTAK dosyadan gelir; rol
// sablonu drawer'i da ayni tablolari kullaniyor. Ayri tutuldugunda sunucuya
// eklenen yeni kategoriler burada ham key olarak kaliyordu.
import {
  PERMISSION_LABELS,
  CATEGORY_ORDER,
  categoryOf,
  categoryLabel as catLabel,
  getSubGroups,
} from "../../utils/permissionCatalog";

// ─── Permission Labels (Turkish) ───
const PERMISSION_LABELS_TR = {
  "dashboard.read": "Dashboard T\u00fcketim Kartlar\u0131",
  "dashboard.graph": "T\u00fcketim Grafi\u011fi",
  "dashboard.deviceStatus": "Cihaz Durum Kartlar\u0131",
  "dashboard.firmStatus": "Firma Durum Kartlar\u0131",
  "dashboard.map": "Cihaz Haritas\u0131",
  "dashboard.billDetails": "Dashboard Fatura Detaylar\u0131",
  "alerts.read": "Uyarılar sayfası",
  "alerts.devices": "Cihaz uyarıları",
  "alerts.firms": "Firma uyarıları (kritik kota, düşük performans)",
  "alerts.orders": "Sipariş vermeyen firmalar",
  "devices.read": "Cihaz listesi görüntüleme",
  "devices.distribution": "Cihaz dağılım matrisi",
  "devices.create": "Yeni cihaz oluşturma",
  "devices.info": "Cihaz bilgi sekmesi",
  "devices.infoFirm": "Cihaz bilgi: firma bilgileri kartı",
  "devices.infoVersion": "Cihaz bilgi: versiyon bilgileri kartı",
  "devices.infoGsm": "Cihaz bilgi: GSM bilgileri kartı",
  "devices.infoNote": "Cihaz bilgi: notlar kartı",
  "devices.infoProducts": "Cihaz bilgi: ürün bilgileri kartları",
  "devices.developerPassword": "Cihaz bilgi: developer parolasını görüntüleme",
  "devices.movement": "Cihaz hareket sekmesi",
  "devices.location": "Cihaz konum sekmesi",
  "devices.settings": "Cihaz ayarlar sekmesi",
  "devices.maintenance": "Cihaz bakım sekmesi (canlı sensör izleme, hassasiyet)",
  "devices.stats": "Cihaz istatistik sekmesi",
  "devices.editNote": "Cihaz notu düzenleme",
  "devices.editName": "Cihaz özel adı değiştirme",
  "devices.editModelName": "Cihaz model ismi değiştirme",
  "devices.editSerial": "Cihaz seri no değiştirme",
  "devices.editQuotaSettings": "Ürün max kota, uyarı kotası, sync seviyesi",
  "devices.editQuotaDistribution": "Kota dağıtım aktifliği değişimi",
  "devices.reports": "Cihaz raporlar sekmesi",
  "devices.reportConsumption": "Cihaz tüketim logları",
  "devices.reportOperation": "Cihaz işlem logları",
  "devices.reportSettings": "Cihaz ayar değişiklik logları",
  "devices.reportQuota": "Cihaz kota logları (yükleme + değişim)",
  "devices.reportInfo": "Cihaz bilgi değişiklik logları",
  "devices.lastOperation": "Cihaz son işlem görüntüleme",
  "devices.toggleActivity": "Cihaz kota almaya aç/kapa",
  "devices.moveToFirm": "Cihazı firmaya ver",
  "devices.moveToService": "Cihazı servise al",
  "devices.moveToPlaymaker": "Cihazı plasiyere ver",
  "devices.moveToStorage": "Cihazı depoya al",
  "devices.delete": "Cihaz silme",
  "devices.quota": "Manuel kota yükleme",
  "firms.read": "Firma listesi görüntüleme",
  "firms.info": "Firma bilgi sekmesi",
  "firms.create": "Yeni firma oluşturma",
  "firms.delete": "Firma silme",
  "firms.editName": "Firma adı değiştirme",
  "firms.editBayserNo": "Firma bayser numarası değiştirme",
  "firms.editDetails": "İletişim, vergi, kurumsal/bireysel, not düzenleme",
  "firms.editActivity": "Firma aktiflik aç/kapat",
  "firms.editAutoReport": "Firma otomatik rapor ayarları düzenleme",
  "firms.editMainFirm": "Ana firma değiştirme",
  "firms.editDealerRelation": "Bayi ilişkisi ekleme/kaldırma",
  "firms.devices": "Firma cihazlar sekmesi",
  "firms.stats": "Firma performans sekmesi",
  "firms.bills": "Firma faturalar sekmesi",
  "firms.productSettings": "Firma ürün ayarları sekmesi",
  "firms.belowFirms": "Alt firmalar/alt bayiler sekmesi",
  "firms.users": "Firma kullanıcıları sekmesi",
  "firms.createUser": "Firma detayından kullanıcı oluşturma",
  "firms.customers": "Müşteri firmaları sekmesi",
  "firms.belowFirmsLink": "Alt firma ekleme",
  "firms.belowFirmsUnlink": "Alt firma çıkarma",
  "firms.employees": "Çalışanlar sekmesi görüntüleme",
  "firms.employeesLink": "Çalışan ekleme (firmaya bağlama)",
  "firms.employeesUnlink": "Çalışan çıkarma (firmadan ayırma)",
  "firms.employeesEditQuota": "Çalışan kota düzenleme",
  "firms.logConsumption": "Firma tüketim logları",
  "firms.logInfo": "Firma bilgi değişikliği logları",
  "firms.logBill": "Firma fatura hareketi logları",
  "firms.logSettings": "Firma ayar değişikliği logları",
  "firms.logOperation": "Firma operasyon logları",
  "firms.logQuota": "Firma kota değişikliği logları",
  "firms.roleTemplatesRead": "Rol şablonları görüntüleme",
  "firms.roleTemplatesCreate": "Rol şablonu oluşturma",
  "firms.roleTemplatesEdit": "Rol şablonu düzenleme",
  "firms.roleTemplatesDelete": "Rol şablonu silme",
  "firms.employeeTemplatesRead": "Çalışan şablonları görüntüleme",
  "firms.employeeTemplatesCreate": "Çalışan şablonu oluşturma",
  "firms.employeeTemplatesEdit": "Çalışan şablonu düzenleme",
  "firms.employeeTemplatesDelete": "Çalışan şablonu silme",
  "firms.editProductSettings": "Fiyat, kota katsayısı, max kota, sync seviyesi, sipariş aralığı, aylık hedef",
  "firms.editProductQuotaDistribution": "Ürün kota almaya açık/kapalı değişimi",
  "firms.editProductQuotaWarning": "Ürün uyarı kotası değişimi",
  "firms.billingSummary": "Firma son fatura özet kartı",
  "firms.consumptionSummary": "Firma kota tüketimi özet kartı",
  "firms.giveQuota": "Bedelsiz kota verme",
  "firms.takeBackQuota": "Bedelsiz kota iade",
  "firms.distributeQuota": "Kota dağıtma",
  "devices.productAutoDistributeQuota": "Cihaz kota dağıtım aktifliği değişimi",
  "bills.read": "Fatura Görüntüleme",
  "bills.create": "Fatura Oluşturma",
  "bills.controll": "Fatura Kontrol",
  "bills.delete": "Fatura Silme",
  "bills.deleteWithoutQuota": "Fatura Silerken Kota Etkisini Atlama",
  "users.read": "Kullan\u0131c\u0131 Listesi G\u00f6r\u00fcnt\u00fcleme",
  "users.detail": "Kullan\u0131c\u0131 Detay Sayfas\u0131",
  "users.createStorage": "Depo Kullanıcısı Oluşturma",
  "users.createDealer": "Bayi Kullanıcısı Oluşturma",
  "users.createCustomer": "Müşteri Kullanıcısı Oluşturma",
  "users.createEnduser": "Son Kullanıcı Oluşturma",
  "users.createMachineworker": "Makine İşçisi Oluşturma",
  "users.edit": "Kullan\u0131c\u0131 Bilgi D\u00fczenleme",
  "users.editAutoReport": "Kullan\u0131c\u0131 otomatik rapor ayarlar\u0131 d\u00fczenleme",
  "users.changeFirm": "Kullan\u0131c\u0131 Firma De\u011fi\u015ftirme",
  "users.changeStatus": "Kullan\u0131c\u0131 Aktiflik De\u011fi\u015ftirme",
  "users.resendInvitation": "Davet Tekrar G\u00f6nderme",
  "users.viewPermissions": "Yetkiler Tab\u0131 G\u00f6r\u00fcnt\u00fcleme",
  "users.activity": "Kullanıcı son hareketleri",
  "users.editPermissions": "Kullan\u0131c\u0131 Yetki D\u00fczenleme",
  "users.assignDevices": "Makine Görevlisine Cihaz Atama",
  "users.delete": "Kullan\u0131c\u0131 Silme",
  "reports.firmBalance": "Müşteri Bakiye Seviyeleri Raporu",
  "reports.firmNew": "Müşteri Eklenenler Raporu",
  "reports.firmBilling": "Yüklenen Faturalar Raporu",
  "reports.firmLocation": "Müşteri Listesi Konum Merkezli Raporu",
  "reports.firmMonthlyConsumption": "Firma Ayl\u0131k T\u00fcketim Raporu",
  "reports.firmDailyConsumption": "Firma G\u00fcnl\u00fck T\u00fcketim Raporu",
  "reports.firmTotalConsumption": "Firma Toplam T\u00fcketim Raporu",
  "reports.firmDailyPerformance": "Müşteri Günlük Performans Raporu",
  "reports.firmPerformance": "Müşteri Aylık Performans Raporu",
  "reports.firmsPerformance": "Müşteriler Yıllık Performans Raporu",
  "reports.deviceConnection": "Cihaz Ba\u011flant\u0131 Bilgileri Raporu",
  "reports.deviceGeneral": "Cihaz Genel Bilgileri Raporu",
  "reports.deviceNew": "Yeni Cihaz Raporu",
  "reports.deviceStatus": "Cihaz Hareket Raporu",
  "reports.deviceServiceStatus": "Cihaz Servis Hareketleri Raporu",
  "reports.devicesAtDate": "Tarihteki Makineler ve Makine Geçmişi Raporu",
  "reports.devicePerformance": "Cihaz Performans Raporu",
  "products.typeRead": "Ürün Tipi Görüntüleme",
  "products.typeCreate": "Ürün Tipi Oluşturma",
  "products.typeEdit": "Ürün Tipi Düzenleme",
  "products.typeDelete": "Ürün Tipi Silme",
  "products.infoRead": "Ürün Bilgisi Görüntüleme",
  "products.infoCreate": "Ürün Bilgisi Oluşturma",
  "products.infoEdit": "Ürün Bilgisi Düzenleme",
  "products.infoDelete": "Ürün Bilgisi Silme",
  "parameters.cityRead": "Şehir Görüntüleme",
  "parameters.cityCreate": "Şehir Oluşturma",
  "parameters.cityEdit": "Şehir Düzenleme",
  "parameters.cityDelete": "Şehir Silme",
  "parameters.townRead": "İlçe Görüntüleme",
  "parameters.townCreate": "İlçe Oluşturma",
  "parameters.townEdit": "İlçe Düzenleme",
  "parameters.townDelete": "İlçe Silme",
  "offers.create": "Teklif Olu\u015fturma",
  "wallet.topup": "Bakiye Y\u00fckleme",
  "scheduler.read": "Zamanlay\u0131c\u0131 G\u00f6r\u00fcnt\u00fcleme",
  "dashboard.scope": "Kapsam Seçici",
  "dashboard.quota": "Bayi Kotası Kartı",
  "factory.overview.read": "Fabrika Genel Bakış Görüntüleme",
  "factory.parts.read": "Fabrika Parça / Envanter Görüntüleme",
  "factory.machines.read": "Fabrika Üretim Kayıtları Görüntüleme",
  "factory.zones.read": "Fabrika Bölge / Tablet Görüntüleme",
  "factory.zones.manage": "Fabrika Bölge Yönetimi",
  "factory.catalog.manage": "Fabrika Katalog Yönetimi (parça/makine tipleri, BOM)",
  "factory.operation.stats": "Fabrika İstatistikleri",
  "factory.operation.cancel.admin": "Fabrika İşlem Geç İptali",
  "factory.audit.read": "Fabrika Denetim Kayıtları",
  "development.read": "Geliştirme Panosu Görüntüleme",
  "tasks.access": "Erişim",
  "tasks.admin": "İş Planlama Yönetimi",
  "factory.action.uretim": "Eylem: Üretim (Tablet)",
  "factory.action.yerlestirme": "Eylem: Yerleştirme (Tablet)",
  "factory.action.ariza-tespit-sokum": "Eylem: Arıza Tespit Söküm (Tablet)",
  "factory.action.temizlik": "Eylem: Temizlik (Tablet)",
  "factory.action.paketleme": "Eylem: Paketleme (Tablet)",
};



// Bayi kullanıcılarında kategori etiketi override'ı. Yeni taksonomide bayi
// yetkileri dealers.*/customers.* altında; `firms` grubunda yalnız rol şablonu
// yetkileri kaldığından "Firmalar" yerine "Rol Şablonları" gösterilir.
const getCategoryLabel = (category, targetUserRole) => {
  if (targetUserRole === "dealer" && category === "firms") return "Rol Şablonları";
  return catLabel(category);
};

// ─── Category order for consistent display ───


/**
 * Group permissions by category prefix (e.g. "devices.read" -> category "devices").
 * @param {string[]} permissions
 * @returns {Record<string, string[]>}
 */
function groupByCategory(permissions) {
  const groups = {};
  for (const perm of permissions) {
    const category = categoryOf(perm);
    if (!groups[category]) groups[category] = [];
    groups[category].push(perm);
  }
  return groups;
}

// ─── Category Card Component (Grid) ───
/** Tek izin satırı — hem düz listede hem alt grup içinde kullanılır. */
function PermissionRow({ perm, selected, onToggle, readOnly, labels }) {
  return (
    <label
      className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-colors ${
        readOnly ? "cursor-default" : "cursor-pointer hover:bg-blue-50/40"
      }`}
    >
      <input
        type="checkbox"
        checked={selected.includes(perm)}
        onChange={() => !readOnly && onToggle(perm)}
        disabled={readOnly}
        className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 shrink-0"
      />
      <span className="text-sm text-gray-600">
        {labels?.[perm] || PERMISSION_LABELS_TR[perm] || PERMISSION_LABELS[perm] || perm}
      </span>
    </label>
  );
}

function CategoryCard({ category, permissions, selected, onToggle, onToggleAll, readOnly, labels, categoryLabel: categoryLabelProp }) {
  // getSubGroups {key} alanına bakar; buradaki liste düz string olduğu için
  // sarmalanır. Tanımlı grup yoksa null döner ve düz listeye düşülür.
  const subGroups = useMemo(
    () => getSubGroups(category, permissions.map((k) => ({ key: k }))),
    [category, permissions]
  );
  const checkedCount = permissions.filter((p) => selected.includes(p)).length;
  const allChecked = checkedCount === permissions.length;
  const someChecked = checkedCount > 0 && !allChecked;

  // Kart yüksekliği sabit h-64 idi: tek izinli kategori kocaman boş dururken
  // 39 izinli Cihazlar kartı sürekli kaydırma istiyordu. Artık içeriğe göre
  // esner, üst sınırda kart içinde kayar.
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-gray-300 hover:shadow-sm transition-all flex flex-col min-h-[12rem] max-h-[24rem]">
      {/* Card header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/50 shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">
            {categoryLabelProp || catLabel(category)}
          </span>
          <span className="text-[11px] text-gray-400 font-medium bg-gray-100 px-1.5 py-0.5 rounded-md">
            {checkedCount}/{permissions.length}
          </span>
        </div>
        <span
          onClick={() => { if (!readOnly) onToggleAll(category, !allChecked); }}
          className="shrink-0"
        >
          <input
            type="checkbox"
            checked={allChecked}
            ref={(el) => { if (el) el.indeterminate = someChecked; }}
            onChange={() => {}}
            disabled={readOnly}
            className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          />
        </span>
      </div>

      {/* Permission items — sabit yükseklikte, taşarsa box içinde kayar.
          Alt grup tanımlı kategorilerde (Cihazlar, Firmalar, Fabrika,
          Programlama Paneli...) izinler başlık altında toplanır; 40+ izinlik
          düz liste taranamıyordu. Gruplama rol şablonu drawer'ıyla ORTAK
          (utils/permissionCatalog.js). */}
      <div className="p-2 flex-1 min-h-0 overflow-y-auto">
        {subGroups ? (
          subGroups.map((sg) => (
            <div key={sg.label} className="mb-2 last:mb-0">
              <div className="px-2.5 pt-1 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                {sg.label}
              </div>
              {sg.items.map((item) => (
                <PermissionRow
                  key={item.key}
                  perm={item.key}
                  selected={selected}
                  onToggle={onToggle}
                  readOnly={readOnly}
                  labels={labels}
                />
              ))}
            </div>
          ))
        ) : (
          permissions.map((perm) => (
            <PermissionRow
              key={perm}
              perm={perm}
              selected={selected}
              onToggle={onToggle}
              readOnly={readOnly}
              labels={labels}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Main PermissionsTab Component ───
export default function PermissionsTab({ user, readOnly: readOnlyProp = false }) {
  const { auth } = useAuth();
  const { isAdmin } = usePermission();
  const { showSuccess, showError } = useAlert();
  const token = auth?.accessToken;

  const targetUserId = user?._id;
  const targetUserRole = user?.role;
  const isTargetAdmin = targetUserRole === "admin";

  // Fetch available permissions
  const {
    data: permissionsData,
    isLoading: isLoadingPermissions,
    isError: isPermissionsError,
  } = useGetAvailablePermissionsQuery(
    { token, id: targetUserId },
    { skip: !token || !targetUserId }
  );

  const [updatePermissions, { isLoading: isSaving }] = useUpdateUserPermissionsMutation();

  // Available permissions from server
  const availablePermissions = useMemo(
    () =>
      (permissionsData?.data?.permissions || []).map((p) =>
        typeof p === "string" ? p : p.key
      ),
    [permissionsData]
  );

  // Yetki etiketleri — server her yetkinin Türkçe `description`'ını gönderir.
  // Böylece hiçbir yetki çevirisiz (ham anahtar) kalmaz.
  const labelMap = useMemo(() => {
    const m = {};
    (permissionsData?.data?.permissions || []).forEach((p) => {
      if (p && typeof p === "object" && p.key) {
        m[p.key] = p.description || p.key;
      }
    });
    return m;
  }, [permissionsData]);

  // Kullanıcının geçerli izinleri — sunucunun hesapladığı küme öncelikli:
  // şablonsuz "Admin" müşteride liste boş durur ama bütün müşteri izinleri
  // geçerlidir (Server #238). Sunucu cevabı yoksa kullanıcı kaydındaki liste.
  const currentPermissions = useMemo(
    () => permissionsData?.data?.currentPermissions || user?.permissions || [],
    [permissionsData, user?.permissions]
  );

  // Local selected state
  const [selected, setSelected] = useState([]);

  // Sync selected state when user data changes
  useEffect(() => {
    setSelected(currentPermissions);
  }, [currentPermissions]);

  // Group available permissions by category
  const grouped = useMemo(() => groupByCategory(availablePermissions), [availablePermissions]);
  const sortedCategories = useMemo(() => {
    const known = CATEGORY_ORDER.filter((c) => grouped[c]?.length > 0);
    // Bilinen sıraya girmeyen kategoriler de gösterilsin (gizlenmesin).
    const extras = Object.keys(grouped).filter(
      (c) => !CATEGORY_ORDER.includes(c) && grouped[c]?.length > 0
    );
    return [...known, ...extras];
  }, [grouped]);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    if (selected.length !== currentPermissions.length) return true;
    const sortedSelected = [...selected].sort();
    const sortedCurrent = [...currentPermissions].sort();
    return sortedSelected.some((p, i) => p !== sortedCurrent[i]);
  }, [selected, currentPermissions]);

  // Toggle a single permission
  const handleToggle = useCallback((perm) => {
    setSelected((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  }, []);

  // Toggle all permissions in a category
  const handleToggleAll = useCallback(
    (category, checked) => {
      const categoryPerms = grouped[category] || [];
      setSelected((prev) => {
        if (checked) {
          const newSet = new Set([...prev, ...categoryPerms]);
          return Array.from(newSet);
        } else {
          return prev.filter((p) => !categoryPerms.includes(p));
        }
      });
    },
    [grouped]
  );

  // Save
  const handleSave = async () => {
    if (!token || !targetUserId || isTargetAdmin) return;
    try {
      await updatePermissions({
        token,
        id: targetUserId,
        permissions: selected,
      }).unwrap();
      showSuccess("Yetkiler ba\u015far\u0131yla g\u00fcncellendi");
    } catch (err) {
      showError(err?.data?.message || "Yetki g\u00fcncelleme s\u0131ras\u0131nda hata olu\u015ftu");
    }
  };

  // Reset
  const handleReset = () => setSelected(currentPermissions);

  // Read-only: admin users veya dışarıdan gelen readOnly prop
  const readOnly = isTargetAdmin || readOnlyProp;

  // Loading state \u2014 ger\u00e7ek box grid'iyle ayn\u0131 iskelet (sabit y\u00fckseklikli kutular)
  if (isLoadingPermissions) {
    const rowW = ["w-3/4", "w-2/3", "w-5/6", "w-1/2", "w-4/6"];
    return (
      <div className="relative min-h-[300px]">
        <MascotLoadingOverlay message="Yetkiler y\u00fckleniyor..." />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col h-64"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/50 shrink-0">
                <div className="h-4 w-28 bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
              </div>
              <div className="p-2 flex-1 space-y-1">
                {[0, 1, 2, 3, 4].map((j) => (
                  <div key={j} className="flex items-center gap-2.5 px-2.5 py-2">
                    <div className="h-4 w-4 bg-gray-200 rounded animate-pulse shrink-0" />
                    <div className={`h-3 ${rowW[j % rowW.length]} bg-gray-200 rounded animate-pulse`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (isPermissionsError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FaShieldAlt className="w-10 h-10 text-gray-300 mb-3" />
        <p className="text-sm text-gray-500">Yetkiler yüklenemedi. Lütfen tekrar deneyin.</p>
      </div>
    );
  }

  // No available permissions
  if (availablePermissions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FaShieldAlt className="w-10 h-10 text-gray-300 mb-3" />
        <p className="text-sm text-gray-500">Bu kullan\u0131c\u0131 i\u00e7in atanabilir yetki bulunmuyor.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-0 gap-4">
      {/* Admin info banner */}
      {isTargetAdmin && (
        <div className="flex items-center gap-3 px-4 py-3 bg-purple-50 border border-purple-200 rounded-xl shrink-0">
          <FaLock className="w-4 h-4 text-purple-500 shrink-0" />
          <p className="text-sm text-purple-700">
            Admin kullanıcılar tüm yetkilere sahiptir. Yetkileri düzenlenemez.
          </p>
        </div>
      )}

      {/* Şablon-tek-kaynak bilgi bandı — yetkiler buradan düzenlenmez */}
      {!isTargetAdmin && readOnly && (
        <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl shrink-0">
          <FaLock className="w-4 h-4 text-blue-500 shrink-0" />
          <p className="text-sm text-blue-700">
            Yetkiler firmanın <strong>Kullanıcı Şablonları</strong> sekmesinden yönetilir.
            Kullanıcının alt rolüne karşılık gelen şablon güncellendiğinde yetkiler otomatik yansır.
          </p>
        </div>
      )}

      {/* Permission grid — scrollable */}
      <div className="flex-1 min-h-0 overflow-y-auto pr-1 relative">
        {isSaving && <MascotLoadingOverlay message="Yetkiler güncelleniyor..." />}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 items-start">
        {sortedCategories.map((category) => (
          <CategoryCard
            key={category}
            category={category}
            categoryLabel={getCategoryLabel(category, targetUserRole)}
            permissions={grouped[category]}
            selected={isTargetAdmin ? availablePermissions : selected}
            onToggle={handleToggle}
            onToggleAll={handleToggleAll}
            readOnly={readOnly}
            labels={labelMap}
          />
        ))}
        </div>
      </div>

      {/* Save bar */}
      {!readOnly && hasChanges && (
        <div className="sticky bottom-0 bg-white border-t border-gray-200 -mx-3 -mb-3 px-4 py-3 sm:px-5 sm:py-3 flex items-center justify-between rounded-b-xl">
          <p className="text-xs sm:text-sm text-gray-500">
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-500" />
              Kaydedilmemi\u015f yetki de\u011fi\u015fiklikleri var
            </span>
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={handleReset}
              disabled={isSaving}
              className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Geri Al
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-1.5 px-4 py-1.5 sm:px-5 sm:py-2 text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl hover:from-indigo-700 hover:to-blue-700 shadow-sm hover:shadow-md transition-all disabled:opacity-50"
            >
              {isSaving ? (
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <FaSave className="w-3.5 h-3.5" />
              )}
              <span>{isSaving ? "Kaydediliyor..." : "Kaydet"}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
